import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForgotPasswordStore } from "../store";
import { resetPasswordReq } from "@/api/services/auth";
import type { ForgotPasswordFormData } from "../store/types";
import { paths } from "@/navigation/paths";

type FieldErrors = Partial<Record<keyof ForgotPasswordFormData, string>>;

export const useForgotPassword = () => {
  const navigate = useNavigate();
  const [fieldsErrors, setFieldsErrors] = useState<FieldErrors>({});
  const { phone, setPhone, setLoading, setError, isLoading, errorMessage } =
    useForgotPasswordStore();

  // Clear error when component mounts
  useEffect(() => {
    setError(null);
  }, [setError]);

  const formData: ForgotPasswordFormData = { phone };

  const onChangePhoneField = (value: string) => {
    setPhone(value);
    if (fieldsErrors.phone) {
      setFieldsErrors({ ...fieldsErrors, phone: undefined });
    }
  };

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await resetPasswordReq({ phone: data.phone });

      if (response.data === true) {
        // Navigate to OTP verification page
        navigate(paths.VERIFY_OTP, {
          state: { phone: data.phone, isForgotPassword: true },
        });
        // Clear the form after successful navigation
        useForgotPasswordStore.getState().reset();
      } else {
        setError("Failed to reset password. Please make sure the account exists.");
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send reset code. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const setFieldErrorsHandler = (errors: FieldErrors) => {
    setFieldsErrors(errors);
  };

  const clearFieldErrorsHandler = () => {
    setFieldsErrors({});
  };

  return {
    formData,
    fieldsErrors,
    onSubmit,
    onChangePhoneField,
    setFieldErrorsHandler,
    clearFieldErrorsHandler,
    isLoading,
    errorMessage,
  };
};
